import { PropsWithChildren, useState } from 'react';
import { Segment, Confirm, Popup } from 'semantic-ui-react';
import { Link } from 'react-router';
import { Button, Icon } from '@britishcouncil/react-admin';
import styled from '@emotion/styled';
import { useMonitoringTableContext } from './useMonitoringTableContext';

export interface MonitoringTableActionsProps {
  onDelete: (selectedIds: Set<number>) => Promise<void>;
  hasArchiveButton?: boolean;
  hasAddButton?: boolean;
  hasDeleteButton?: boolean;
  archiveRoute?: string;
  addRoute?: string;
  deleteConfirmContent?: string;
  deleteConfirmHeader?: string;
  archiveDisabled?: boolean;
  archiveDisabledMessage?: string;
}

export const MonitoringTableActions = ({
  onDelete,
  hasArchiveButton = true,
  hasAddButton = true,
  hasDeleteButton = true,
  archiveRoute = 'archive',
  addRoute = 'add',
  deleteConfirmContent = 'You are going to delete the selected monitoring records. Do you want to continue?',
  deleteConfirmHeader = 'Confirmation',
  archiveDisabled = false,
  archiveDisabledMessage = 'No current monitorings to be archived',
  children,
}: PropsWithChildren<MonitoringTableActionsProps>) => {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const { selectedIds } = useMonitoringTableContext();

  const handleDelete = async () => {
    try {
      await onDelete(selectedIds);
      setDeleteConfirmOpen(false);
    } catch (error) {
      console.error('Failed to delete monitoring records:', error);
    }
  };

  return (
    <>
      <S.Segment>
        <S.FirstFlexItem>{children}</S.FirstFlexItem>
        {hasDeleteButton && (
          <Popup
            content="Please select records to be deleted"
            trigger={
              <span>
                <Button
                  disabled={selectedIds.size === 0}
                  color="red"
                  onClick={() => setDeleteConfirmOpen(true)}
                >
                  Delete selected entries ({selectedIds.size})
                </Button>
              </span>
            }
            disabled={selectedIds.size > 0}
          />
        )}

        {hasArchiveButton && (
          <Popup
            content={archiveDisabledMessage}
            trigger={
              <span>
                <Button color="yellow" disabled={archiveDisabled} as={Link} to={archiveRoute}>
                  <Icon name="archive" /> Archive
                </Button>
              </span>
            }
            disabled={!archiveDisabled}
          />
        )}

        {hasAddButton && (
          <Button primary icon as={Link} to={addRoute}>
            <Icon name="plus" /> Add monitoring
          </Button>
        )}
      </S.Segment>

      <Confirm
        open={deleteConfirmOpen}
        header={deleteConfirmHeader}
        content={deleteConfirmContent}
        onCancel={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        confirmButton="Yes"
        cancelButton="Cancel"
      />
    </>
  );
};

const S = {
  Segment: styled(Segment)`
    && {
      display: flex;
      gap: 10px;
      justify-content: flex-end;
    }
  `,
  FirstFlexItem: styled.div`
    flex-grow: 1;
  `,
};
